import { useContext, useRef } from "react";

import Container from "react-bootstrap/Container";
import Row from "react-bootstrap/Row";
import Col from "react-bootstrap/Col";
import Card from "react-bootstrap/Card";
import InputGroup from "react-bootstrap/InputGroup";
import Form from "react-bootstrap/Form";

import { useQuery } from "@apollo/client";

import { useSearchParams } from "react-router-dom";

import { BsSearch } from "react-icons/bs";

import {
  InfiniteScrollingList,
  Button 
} from "@evos/components";
import Header from "components/Header";
import { useWindowSize } from "@evos/hooks";

import { AuthContext } from "lib/context/auth";
import { SERVICE_LOGS } from "./gql";

import type { 
  ServiceLogsConnection,
} from "types";

function useAuth() {
  return useContext(AuthContext);
}

function buildWhereArgument(searchString: string | null) {
  return searchString != null && searchString.length > 0
    ? { 
      where: {
        _or: [
          {cluster_name: {like: searchString}},
          {code: {like: searchString}},
          {container_name: {like: searchString}},
          {description: {like: searchString}},
          {severity: {like: searchString}}
        ]
      }
    }
    : null;
}

export default function ErrorList() {
  const auth = useAuth();
  const darkMode = auth?.darkMode ?? false;
  const windowWidth = useWindowSize().width ?? window.innerWidth;

  const [ searchParams, setSearchParams ] = useSearchParams();
  const searchInputRef = useRef<HTMLInputElement>(null);
  const searchQueryString = searchParams.get("search") ?? "";

  const { 
    data: serviceLogsData, 
    networkStatus: serviceLogsNetworkStatus, 
    error: serviceLogsError, 
    fetchMore
  } = useQuery<ServiceLogsConnection>(
    SERVICE_LOGS,
    { 
      variables: {
        n: 15, 
        after: null,
        ...((searchQueryString.length > 0) && buildWhereArgument(searchQueryString))
      },
      fetchPolicy: "cache-and-network",
      notifyOnNetworkStatusChange: true,
    }
  );

  const serviceLogsInitialLoading = serviceLogsNetworkStatus === 1;
  const serviceLogsFetchMoreLoading = serviceLogsNetworkStatus === 3;

  const pageInfoObject = serviceLogsData?.ServiceLogs?.pageInfo;

  const serviceLogsDataList = serviceLogsData?.ServiceLogs?.edges?.map(d => ({
    severity: d?.node?.severity ?? "No severity provided",
    timestamp: d?.node?.receive_timestamp ?? "No timestamp provided",
    summary: {
      cluster_name: d?.node?.cluster_name ?? "No cluster name provided",
      container_name: d?.node?.container_name ?? "No container name provided",
      description: d?.node?.description ?? "No description provided",
      context: JSON.parse(d?.node?.context) ?? "No context provided",
    }
  }));

  const handleSearch = (searchString: string | null) => {
    if (searchString != null && searchString.length > 0) {
      setSearchParams({search: searchString });
    } else {
      setSearchParams({});
    }
  };

  const handleFetchMore = (pageInfo: ServiceLogsConnection["ServiceLogs"]["pageInfo"]) => {
    if (pageInfo?.hasNextPage === true) {
      fetchMore({
        variables: {
          n: 15,
          after: pageInfo.endCursor,
          ...((searchQueryString.length > 0) && buildWhereArgument(searchQueryString))
        }
      });
    }
  };

  return (
    <div id="Settings">
      <Header />
      <Container>
        <Row className="py-4">
          <Col xs={12} className="mb-2">
            <Row className="pb-1 justify-content-start">
              <Col xs={12} md={6} xl={7} xxl={8} five-xl={9} className="d-flex justify-content-start py-2">
                <h1 className="mb-0" style={{fontSize: "1.6em"}}>Service Error Logs</h1>
              </Col>
            </Row>
          </Col>
          <Col xs={12}>
            <Row className="mb-3">
              <Col xs={12} lg={5}>
                <InputGroup className="pt-2 pt-sm-0">
                  <Form.Control
                    defaultValue={searchQueryString}
                    placeholder="Search on all fields..."
                    aria-label="Search on all fields..."
                    aria-describedby="basic-search"
                    ref={searchInputRef}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        if (searchInputRef?.current?.value != null && searchInputRef.current.value.length > 0) {
                          handleSearch(searchInputRef.current.value);
                        } else {
                          handleSearch(null);
                        }
                      }
                    }}
                  />
                  <Button
                    variant="primary"
                    className="px-3 py-2 fw-semibold"
                    id="button-search"
                    isLoading={serviceLogsInitialLoading || serviceLogsFetchMoreLoading}
                    onClick={() => {
                      if (searchInputRef?.current?.value != null && searchInputRef.current.value.length > 0) {
                        handleSearch(searchInputRef.current.value);
                      } else {
                        handleSearch(null);
                      }
                    }}>
                    <BsSearch />
                  </Button>
                </InputGroup>
              </Col>
            </Row>
            <Card className={`pb-3 mt-4 ${darkMode ? "bg-dark" : ""}`}>
              <InfiniteScrollingList
                data={serviceLogsDataList}
                fetchMoreData={handleFetchMore}
                initialLoading={serviceLogsInitialLoading}
                fetchMoreLoading={serviceLogsFetchMoreLoading}
                pageSize={15}
                error={serviceLogsError}
                listItemWidths="0.2fr 0.4fr 1fr"
                windowWidth={windowWidth}
                pageInfo={pageInfoObject}
              />
            </Card>
          </Col>
        </Row>
      </Container>
    </div>
  );
}
